import React, { Children } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { motion } from 'framer-motion';
import { Heart, Dumbbell, Target, Trophy, Users, Handshake, Brain, UserCheck, Shield, Smile, Activity, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
export function FamilyBenefitsPage() {
  const benefits = [{
    icon: Dumbbell,
    title: 'Physical Health and Fitness',
    items: [{
      subtitle: 'Improved Coordination and Strength',
      description: 'Martial arts training involves movements that develop coordination, strength, flexibility, and endurance, all of which benefit family members of all ages.'
    }, {
      subtitle: 'Healthy Lifestyle Habits',
      description: 'Regular practice encourages healthy habits like routine exercise, proper nutrition, and good sleep, which can positively influence everyone in the family.'
    }, {
      subtitle: 'Stress Relief',
      description: "Martial arts can help release tension and stress, promoting overall mental health. It's a fun, engaging way to stay fit and keep energy levels high."
    }]
  }, {
    icon: Target,
    title: 'Discipline and Self-Control',
    items: [{
      subtitle: 'Structure and Routine',
      description: 'Martial arts provide a structured environment where participants must follow specific rules and guidelines, fostering discipline. This is especially beneficial for children, helping them develop respect for authority, rules, and others.'
    }, {
      subtitle: 'Self-Regulation',
      description: 'Training teaches self-control—an essential life skill. This can translate into better decision-making, anger management, and more self-confidence in all aspects of life.'
    }]
  }, {
    icon: Trophy,
    title: 'Building Confidence and Self-Esteem',
    items: [{
      subtitle: 'Achieving Goals',
      description: 'Martial arts often have a system of belts or ranks, which provides tangible markers of progress. As family members achieve higher ranks, they gain confidence in their abilities.'
    }, {
      subtitle: 'Personal Growth',
      description: 'Through overcoming challenges in training, whether physical or mental, martial artists often develop a strong sense of accomplishment, which boosts their self-esteem.'
    }]
  }, {
    icon: Users,
    title: 'Bonding and Family Time',
    items: [{
      subtitle: 'Shared Activities',
      description: 'Martial arts classes or even home training can be a unique way for family members to spend quality time together. This shared interest strengthens bonds and creates opportunities for positive interaction.'
    }, {
      subtitle: 'Teamwork and Support',
      description: "While martial arts are often an individual pursuit, there's also a strong emphasis on supporting one another. Families can encourage each other to succeed, and parents can model perseverance and resilience to their children."
    }]
  }, {
    icon: Handshake,
    title: 'Conflict Resolution and Respect',
    items: [{
      subtitle: 'Respect for Others',
      description: 'Martial arts emphasize respect for teachers, peers, and oneself. This cultural value can foster greater mutual respect within the family.'
    }, {
      subtitle: 'Conflict Management',
      description: 'The principles taught in martial arts—such as de-escalation, focus, and control—can be applied to resolving conflicts calmly, whether at home, in school, or in social settings.'
    }]
  }, {
    icon: Brain,
    title: 'Mental Toughness and Focus',
    items: [{
      subtitle: 'Concentration',
      description: 'Martial arts require intense focus and mental clarity. Practitioners learn to stay present and concentrate on the task at hand, which can improve focus in school or at work.'
    }, {
      subtitle: 'Perseverance and Resilience',
      description: 'Martial arts are about more than just physical strength; they teach mental toughness, encouraging practitioners to keep going even when things get tough.'
    }]
  }, {
    icon: UserCheck,
    title: 'Improved Social Skills',
    items: [{
      subtitle: 'Respecting Differences',
      description: 'Martial arts often bring together people from different backgrounds, teaching students how to interact with a diverse group of people in a respectful and positive way.'
    }, {
      subtitle: 'Community Building',
      description: 'Participating in martial arts creates a sense of community. Family members can connect with others who share similar interests, expanding their social network and encouraging positive social interactions.'
    }]
  }, {
    icon: Shield,
    title: 'Safety Awareness',
    items: [{
      subtitle: 'Personal Safety',
      description: 'Learning self-defense techniques provides practical skills for family members to stay safe in potentially dangerous situations. This can give both children and adults confidence when navigating public spaces.'
    }, {
      subtitle: 'Stranger Awareness and Stranger Safety',
      description: 'Martial arts programs often teach situational awareness and how to handle uncomfortable or dangerous situations, empowering families to make better safety decisions.'
    }]
  }, {
    icon: Heart,
    title: 'Positive Role Modeling',
    items: [{
      subtitle: 'Parental Involvement',
      description: 'When parents practice martial arts alongside their children, it sets a great example for them. Children are more likely to adopt positive habits when they see their parents taking part in activities that promote personal growth.'
    }, {
      subtitle: 'Leadership Skills',
      description: 'Parents or older children who advance in their martial arts journey may have the opportunity to take on leadership roles, teaching younger family members or beginners, which builds leadership qualities.'
    }]
  }, {
    icon: Smile,
    title: 'Stress-Free Fun',
    items: [{
      subtitle: 'Engaging and Enjoyable',
      description: 'Martial arts is not just about hard work and discipline; it can also be a lot of fun! The challenge, camaraderie, and enjoyment of learning new techniques or participating in sparring or tournaments can provide a great outlet for family fun.'
    }]
  }];
  return <div className="bg-black min-h-screen" data-id="element-433">
      <Navigation data-id="element-434" />

      {/* Hero Section */}
      <div className="relative pt-20 pb-16 overflow-hidden" data-id="element-435">
        <div className="absolute inset-0 z-0" data-id="element-436">
          <img src="https://cdn.magicpatterns.com/uploads/nxhzAZmmNrmxVD3DunjYRa/familybenefits.png" alt="Family Benefits" className="w-full h-full object-cover opacity-20" data-id="element-437" />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/90 to-black" data-id="element-438" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16" data-id="element-439">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-center" data-id="element-440">
            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6" data-id="element-441">
              Benefits for <span className="text-[#00D4FF]" data-id="element-442">Families</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed" data-id="element-443">
              Walker's Martial Arts can offer a wide range of benefits for
              families, from physical fitness to emotional well-being and
              bonding. Here are some of the key ways Walker's Martial Arts can
              positively impact family dynamics:
            </p>
          </motion.div>
        </div>
      </div>

      {/* Benefits Sections */}
      <section className="py-20 bg-[#0a0a0a]" data-id="element-444">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-445">
          <div className="space-y-20" data-id="element-446">
            {benefits.map((benefit, index) => <motion.div key={benefit.title} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6,
            delay: index * 0.05
          }} className="relative" data-id="element-447">
                {/* Section Header */}
                <div className="flex items-center gap-4 mb-8" data-id="element-448">
                  <div className="flex items-center justify-center w-14 h-14 bg-[#00D4FF]/10 rounded-2xl border border-[#00D4FF]/30" data-id="element-449">
                    <benefit.icon className="w-7 h-7 text-[#00D4FF]" data-id="element-450" />
                  </div>
                  <div data-id="element-451">
                    <span className="text-[#00D4FF] font-bold text-lg" data-id="element-452">
                      {index + 1}.
                    </span>
                    <h2 className="text-3xl font-bold text-white inline ml-3" data-id="element-453">
                      {benefit.title}
                    </h2>
                  </div>
                </div>

                {/* Items */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ml-0 md:ml-18" data-id="element-454">
                  {benefit.items.map((item, itemIndex) => <motion.div key={item.subtitle} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: itemIndex * 0.1
              }} className="bg-[#1a1a1a] p-6 rounded-xl border border-gray-800" data-id="element-455">
                      <h3 className="text-lg font-bold text-white mb-3 flex items-start gap-2" data-id="element-456">
                        <Zap className="w-5 h-5 text-[#00D4FF] flex-shrink-0 mt-0.5" data-id="element-457" />
                        {item.subtitle}
                      </h3>
                      <p className="text-gray-400 leading-relaxed text-sm" data-id="element-458">
                        {item.description}
                      </p>
                    </motion.div>)}
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Summary Section */}
      <section className="py-20 bg-black" data-id="element-459">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-460">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center" data-id="element-461">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#00D4FF]/10 rounded-full mb-6" data-id="element-462">
              <Heart className="w-8 h-8 text-[#00D4FF]" data-id="element-463" />
            </div>
            <p className="text-2xl text-gray-300 leading-relaxed mb-12" data-id="element-464">
              Overall, martial arts can be a powerful tool for strengthening
              family bonds, improving individual well-being, and fostering a
              supportive, respectful, and active family environment. Whether
              practiced together or individually, the values and skills learned
              in martial arts can have lasting positive effects on the entire
              family.
            </p>

            <div className="bg-gradient-to-r from-[#00D4FF]/10 to-transparent p-8 rounded-2xl border border-[#00D4FF]/20 mb-12" data-id="element-465">
              <p className="text-xl text-white font-semibold mb-6" data-id="element-466">
                Bring your family together through martial arts
              </p>
              <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-[#00D4FF] text-black font-bold rounded-full hover:bg-[#00D4FF]/90 transition-colors" data-id="element-467">
                <Activity className="w-5 h-5" data-id="element-468" />
                Start Your Family Journey
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-469" />
    </div>;
}